import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Box,
  Hidden,
  CardContent,
  CardHeader,
  Card,
  Divider,
  Alert,
  AlertColor,
  Toolbar
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { connect } from 'react-redux';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../store/actions/alertActions';
import FleetIoService from '../services/FleetIoService';
import { formatErrorMessage, getDaysFromNow } from '../utils/common';
import { hide, show } from '../store/actions/busyIndicatorActions';
import GlobalSearch from '../components/GlobalSearch';

interface Props {
  showAlert: (message: any) => any,
  clearAllAlerts: () => any,
  showBusyIndicator: () => any,
  hideBusyIndicator: () => any,
  fleetIoId: number,
  stateId: number,
  odometer: number
}

interface ServiceReminder {
  id: number;
  vehicle_id: number;
  service_task_id: number;
  next_due_at: string;
  due_soon_at: string;
  service_reminder_status: string;
  next_due_primary_meter_value: string;
  due_soon_primary_meter_value: string;
  created_at: string;
  updated_at: string;
  vehicle: {
    id: number;
    name: string;
    license_plate: string;
    make: string;
    model: string;
    year: number;
  };
  service_task: {
    id: number;
    name: string;
    description: string;
  };
}

interface Warning {
  type: AlertColor;
  category: string;
  message: string;
}

const VehicleReminders: React.FC<Props> = ({
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
  fleetIoId,
  stateId,
  odometer
}) => {
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});
  const [remindersData, setRemindersData] = useState<{ records: ServiceReminder[] }>({ records: [] });
  const [warningsData, setWarningsData] = useState<Warning[]>([]);
  const [searchText, setSearchText] = useState('');
  const [filterByCol, setFilterByCol] = useState('all_col');
  const [filteredRecords, setFilteredRecords] = useState<ServiceReminder[]>([]);

  const columns = [
    {
      field: 'vehicle_name',
      headerName: 'Vehicle'
    },
    {
      field: 'service_task',
      headerName: 'Service Task'
    },
    {
      field: 'next_due_at',
      headerName: 'Next Due At'
    },
    {
      field: 'due_soon_at',
      headerName: 'Due Soon At'
    },
    {
      field: 'status',
      headerName: 'Status'
    }
  ];

  const getColValue = (record: ServiceReminder, col: string) => {
    switch (col) {
      case 'vehicle_name':
        return record.vehicle?.name || '';
      case 'service_task':
        return record.service_task?.name || '';
      case 'next_due_at':
        return new Date(record.next_due_at).toLocaleString() || '';
      case 'due_soon_at':
        return new Date(record.due_soon_at).toLocaleString() || '';
      case 'status':
        return record.service_reminder_status || '';
      default:
        return '';
    }
  };

  const onSearchTextChange = (event: any) => {
    const text = event.target.value.toLowerCase();
    setSearchText(text);

    if (!text) {
      setFilteredRecords(remindersData?.records || []);
      return;
    }

    const filtered = (remindersData?.records || []).filter((record: ServiceReminder) => {
      // Filter only via selected column
      if (filterByCol !== 'all_col') {
        return getColValue(record, filterByCol).toLowerCase().includes(text);
      }

      // Filter over all searchable fields
      return (record.vehicle?.name || '').toLowerCase().includes(text)
        || (record.service_task?.name || '').toLowerCase().includes(text)
        || (new Date(record.next_due_at).toLocaleString() || '').toLowerCase().includes(text)
        || (new Date(record.due_soon_at).toLocaleString() || '').toLowerCase().includes(text)
        || (record.service_reminder_status || '').toLowerCase().includes(text)
        || (record.vehicle?.license_plate || '').toLowerCase().includes(text)
        || (record.service_task?.description || '').toLowerCase().includes(text);
    });

    setFilteredRecords(filtered);
  };

  const onFilterByColumnChange = (col: string) => {
    setFilterByCol(col);
    // Re-run search with new column filter
    onSearchTextChange({ target: { value: searchText } });
  };

  const handleRowClick = (id: number) => {
    setOpenRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const refreshData = () => {
    showBusyIndicator();
    const payload = {
      actionId: 300,
      stateId,
      queryParams: {
        vehicleId: fleetIoId.toString()
      }
    };

    FleetIoService.fleetIoPage(payload)
      .then((resp: any) => {
        setRemindersData(resp);
        setFilteredRecords(resp?.records || []);
        hideBusyIndicator();

        const now = new Date();
        const warnings: Warning[] = [];

        if (resp && resp?.records?.length > 0) {
          resp?.records?.forEach((reminder: ServiceReminder) => {
            const dueSoonAt = reminder?.due_soon_at ? new Date(reminder.due_soon_at) : null;
            const nextDueAt = reminder.next_due_at ? new Date(reminder.next_due_at) : null;

            if (nextDueAt && nextDueAt < now) {
              // eslint-disable-next-line prefer-template
              const message = reminder?.service_task?.name + ' is overdue by ' + getDaysFromNow(reminder?.next_due_at) + ' days.';
              warnings.push({
                type: 'error',
                category: 'insurance',
                message,
              });
            } else if (dueSoonAt && dueSoonAt < now) {
              // eslint-disable-next-line prefer-template
              const message = reminder?.service_task?.name + ' due in ' + getDaysFromNow(reminder?.next_due_at) + ' days.';
              warnings.push({
                type: 'warning',
                category: 'insurance',
                message,
              });
            }

            if (reminder?.next_due_primary_meter_value && reminder?.next_due_primary_meter_value !== '' && odometer > Number(reminder?.next_due_primary_meter_value)) {
              // eslint-disable-next-line prefer-template
              const message = reminder?.service_task?.name + ' is overdue by ' + (odometer - Number(reminder?.next_due_primary_meter_value)) + ' Kms.';
              warnings.push({
                type: 'error',
                category: 'service',
                message
              });
            } else if (reminder?.next_due_primary_meter_value && reminder?.next_due_primary_meter_value !== '' && odometer > Number(reminder?.due_soon_primary_meter_value)) {
              // eslint-disable-next-line prefer-template
              const message = reminder?.service_task?.name + ' is due in ' + (Number(reminder?.next_due_primary_meter_value) - odometer) + ' Kms.';
              warnings.push({
                type: 'warning',
                category: 'service',
                message
              });
            }
          });
        }

        setWarningsData(warnings);
      }).catch((err: any) => {
        hideBusyIndicator();
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      });
  };

  useEffect(() => {
    refreshData();
  }, []);

  return (
    <>
      {remindersData && (
        <div>
          <Toolbar style={{ justifyContent: 'flex-end' }}>
            <GlobalSearch
              searchedText={searchText}
              onSearchTextChange={onSearchTextChange}
              showFilterByColumn
              columns={columns}
              filterByColumn={filterByCol}
              onFilterByColumnChange={onFilterByColumnChange}
              width="300px"
            />
          </Toolbar>

          {warningsData?.map((alert) => (
            <Alert sx={{ margin: 1 }} key={alert.message} severity={alert.type}>{alert.message}</Alert>
          ))}

          <Hidden mdDown>
            {/* Desktop/Table Layout */}
            <TableContainer component={Paper} elevation={3} sx={{ mt: 2, borderRadius: 2 }}>
              <Table aria-label="collapsible table">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell />
                    <TableCell><strong>Vehicle</strong></TableCell>
                    <TableCell><strong>Service Task</strong></TableCell>
                    <TableCell><strong>Next Due At</strong></TableCell>
                    <TableCell><strong>Due Soon At</strong></TableCell>
                    <TableCell><strong>Next Due Odometer Kms</strong></TableCell>
                    <TableCell><strong>Due Soon Odometer Kms</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((record) => (
                      <React.Fragment key={record.id}>
                        <TableRow>
                          <TableCell>
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => handleRowClick(record.id)}
                            >
                              {openRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                            </IconButton>
                          </TableCell>
                          <TableCell>{record.vehicle.name}</TableCell>
                          <TableCell>{record.service_task.name}</TableCell>
                          <TableCell>{new Date(record.next_due_at).toLocaleString()}</TableCell>
                          <TableCell>{new Date(record.due_soon_at).toLocaleString()}</TableCell>
                          <TableCell>{record.next_due_primary_meter_value || '--'}</TableCell>
                          <TableCell>{record.due_soon_primary_meter_value || '--'}</TableCell>
                          <TableCell>{record.service_reminder_status}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                            <Collapse in={openRows[record.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <Typography variant="h6" gutterBottom>Details</Typography>
                                <Typography variant="body2"><strong>Description:</strong> {record.service_task.description}</Typography>
                                <Typography variant="body2"><strong>Vehicle Details:</strong> {record.vehicle.make} {record.vehicle.model} ({record.vehicle.year}) - {record.vehicle.license_plate}</Typography>
                                <Typography variant="body2"><strong>Created At:</strong> {new Date(record.created_at).toLocaleString()}</Typography>
                                <Typography variant="body2"><strong>Updated At:</strong> {new Date(record.updated_at).toLocaleString()}</Typography>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body1" sx={{ py: 2 }}>No reminders found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>

          <Hidden mdUp>
            {/* Mobile/Responsive Layout */}
            {filteredRecords.length > 0 ? (
              filteredRecords.map((record) => (
                <Card key={record.id} sx={{ mb: 2 }}>
                  <CardHeader
                    title={`Vehicle: ${record.vehicle.name}`}
                    subheader={`Service: ${record.service_task.name}`}
                    action={(
                      <IconButton onClick={() => handleRowClick(record.id)}>
                        {openRows[record.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    )}
                  />
                  <Divider />
                  <CardContent>
                    <Typography variant="body2"><strong>Next Due At:</strong> {new Date(record.next_due_at).toLocaleString()}</Typography>
                    <Typography variant="body2"><strong>Due Soon At:</strong> {new Date(record.due_soon_at).toLocaleString()}</Typography>
                    <Typography variant="body2"><strong>Next Due Odometer Kms:</strong> {record?.next_due_primary_meter_value}</Typography>
                    <Typography variant="body2"><strong>Due Soon Odometer Kms:</strong> {record?.due_soon_primary_meter_value}</Typography>
                    <Typography variant="body2"><strong>Status:</strong> {record.service_reminder_status}</Typography>
                    <Collapse in={openRows[record.id]} timeout="auto" unmountOnExit>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6">Details</Typography>
                        <Typography variant="body2"><strong>Description:</strong> {record.service_task.description}</Typography>
                        <Typography variant="body2"><strong>Vehicle Details:</strong> {record.vehicle.make} {record.vehicle.model} ({record.vehicle.year}) - {record.vehicle.license_plate}</Typography>
                        <Typography variant="body2"><strong>Created At:</strong> {new Date(record.created_at).toLocaleString()}</Typography>
                        <Typography variant="body2"><strong>Updated At:</strong> {new Date(record.updated_at).toLocaleString()}</Typography>
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="body1" sx={{ py: 2 }}>No reminders found.</Typography>
            )}
          </Hidden>
        </div>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  showAlert: (message: any) => dispatch(showGlobalAlert(message)),
  clearAllAlerts: () => dispatch(clearAllGlobalAlerts()),
  showBusyIndicator: () => dispatch(show()),
  hideBusyIndicator: () => dispatch(hide()),
});

export default connect(null, mapDispatchToProps)(VehicleReminders);
